import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut as fbSignOut,
  type User,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

const EMAIL_STORAGE_KEY = "efa_signin_email";

interface AuthState {
  user: User | null;
  email: string | null;
  loading: boolean;
  isAdmin: boolean;
  sendLink: (email: string) => Promise<void>;
  completeSignInFromLink: (href: string, fallbackEmail?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** Where the magic link returns the user to. */
function actionCodeSettings() {
  return {
    url: `${window.location.origin}/finish-signin`,
    handleCodeInApp: true,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u?.email) {
        try {
          const snap = await getDoc(doc(db, "quiz_admins", u.email.toLowerCase()));
          setIsAdmin(snap.exists());
        } catch {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });
  }, []);

  async function sendLink(email: string) {
    const normalized = email.trim().toLowerCase();
    await sendSignInLinkToEmail(auth, normalized, actionCodeSettings());
    window.localStorage.setItem(EMAIL_STORAGE_KEY, normalized);
  }

  async function completeSignInFromLink(href: string, fallbackEmail?: string) {
    if (!isSignInWithEmailLink(auth, href)) {
      throw new Error("invalid-link");
    }
    let email =
      window.localStorage.getItem(EMAIL_STORAGE_KEY) ||
      fallbackEmail?.trim().toLowerCase();
    if (!email) throw new Error("email-required");
    await signInWithEmailLink(auth, email, href);
    window.localStorage.removeItem(EMAIL_STORAGE_KEY);
  }

  async function signOut() {
    await fbSignOut(auth);
  }

  const value = useMemo<AuthState>(
    () => ({
      user,
      email: user?.email ?? null,
      loading,
      isAdmin,
      sendLink,
      completeSignInFromLink,
      signOut,
    }),
    [user, loading, isAdmin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { EMAIL_STORAGE_KEY };
